import type { ComputedRef, Ref } from 'vue'
import {
  flowFor,
  gateFor,
  hasUnansweredSteps,
  pendingReminders,
  resumeScreen,
  welcomeVariant,
  type FlowScreen,
  type GateSession,
  type OnboardingGate,
  type OptionalStep,
  type WelcomeVariant,
} from '#shared/onboarding/flow'
import type { OnboardingStepOutcome } from '#shared/onboarding/actions'
import type { ProfileStatus } from './useUserProfile'

/**
 * The onboarding gate plus the plan-driven flow behind it (MAR-933).
 *
 * `/api/profile` (via `useUserProfile`) is the single source of truth. The rules — which step the
 * user owes, which screens the setup page walks, which reminders stay behind — are pure functions in
 * `shared/onboarding/flow.ts`; this composable only binds them to the profile and sends the writes.
 *
 * The gate keeps its fail-closed semantics for the REQUIRED steps (`invite` → `org` → `name`) and
 * adds one: `welcome`, for a user who has not yet entered the optional phase. Once they have,
 * leaving counts as skipping and nothing routes them again.
 */
export type OnboardingStep = OnboardingGate

/** A `leave` in flight (O4) — module-wide, so two quick navigations send it once. Client-only state. */
let leaving: Promise<void> | null = null

/**
 * Kept as a named export for tests — the rule is `gateFor`, taking the profile's status rather than
 * reading it, so it stays a function of its arguments.
 */
export const __stepFor = (status: ProfileStatus, profile: Parameters<typeof gateFor>[1]): OnboardingStep => gateFor(status, profile)

/** Per-app state when Nuxt is there (never shared between SSR requests); a plain ref in a bare component test. */
function sessionFlag(key: string): Ref<boolean> {
  try {
    if (typeof useState === 'function') return useState<boolean>(key, () => false)
  } catch {
    // Outside a Nuxt app (a component unit test).
  }
  return ref(false)
}

export function useOnboarding() {
  const { profile, status, ensure, updateProfile } = useUserProfile()
  /**
   * "Asigna tus licencias" was answered in this session (MAR-1404, `GateSession`): the gate moves on before the
   * stored answer (`licences_answered`) comes back on the refreshed profile.
   */
  const licencesAnswered = sessionFlag('onboarding-licences-answered')
  const session = (): GateSession => ({ licencesAnswered: licencesAnswered.value })

  /** Imperative check for route middleware — ensures the profile is loaded first. */
  async function nextStep(): Promise<OnboardingStep> {
    await ensure()
    return gateFor(status.value, profile.value, session())
  }

  /** Reactive step for the setup page — recomputes as the profile updates. */
  const step: ComputedRef<OnboardingStep> = computed(() => gateFor(status.value, profile.value, session()))
  /**
   * The founder assigned themselves a licence, or opted out ("Sin licencia"): the gate moves past `licences` at
   * once, and the answer is STORED (`licences_answered`), so a reload or a new session does not ask again.
   */
  const answerLicences = async () => {
    licencesAnswered.value = true
    await updateProfile({ onboarding: { action: 'licences_answered' } })
  }

  /** Every screen the setup page walks for this user, in order. Empty until a profile is loaded. */
  const flow = computed<FlowScreen[]>(() => (profile.value ? flowFor(profile.value) : []))
  const resumeAt = computed<FlowScreen>(() => (profile.value ? resumeScreen(profile.value) : 'welcome'))
  const variant = computed<WelcomeVariant>(() => (profile.value ? welcomeVariant(profile.value) : 'new'))
  /** Reminders still to show, in step order — the rail shows the first. */
  const reminders = computed<OptionalStep[]>(() => (profile.value ? pendingReminders(profile.value) : []))

  // ── Writes — the only moments onboarding state changes. Each returns the fresh profile. ──
  const start = () => updateProfile({ onboarding: { action: 'start' } })
  /**
   * What the step did, recorded with its outcome — the only record a reload can rebuild from: the
   * clients step's picks (§20, F2) and creations (F3), the cases step's creations (O7).
   */
  const recordStep = (step: OptionalStep, outcome: OnboardingStepOutcome, ids: StepIds = {}) =>
    updateProfile({
      onboarding: {
        action: 'step', step, outcome,
        ...(ids.picked_client_ids?.length ? { picked_client_ids: ids.picked_client_ids } : {}),
        ...(ids.created_client_ids?.length ? { created_client_ids: ids.created_client_ids } : {}),
        ...(ids.created_case_ids?.length ? { created_case_ids: ids.created_case_ids } : {}),
      },
    })
  const finish = () => updateProfile({ onboarding: { action: 'finish' } })
  /**
   * O4: the user is somewhere other than `/app/setup` with a step still unanswered — record those as
   * skipped, once. Called by the route middleware; `hasUnansweredSteps` is false afterwards, so no
   * later navigation writes again. Best-effort: an unrecorded leave reads the same (a reminder).
   */
  function leaveIfMidFlow(): void {
    if (leaving || !profile.value || !hasUnansweredSteps(profile.value)) return
    leaving = updateProfile({ onboarding: { action: 'leave' } }).catch(() => {}).finally(() => { leaving = null })
  }
  /** Done summary (MAR-978): take one client back out of the clients step's recorded picks. */
  const unpickClient = (clientId: string) => updateProfile({ onboarding: { action: 'unpick_client', client_id: clientId } })
  const dismissReminder = (step: OptionalStep) => updateProfile({ onboarding: { action: 'dismiss_reminder', step } })
  const replay = () => updateProfile({ onboarding: { action: 'replay' } })

  return { nextStep, step, answerLicences, flow, resumeAt, variant, reminders, start, recordStep, finish, unpickClient, leaveIfMidFlow, dismissReminder, replay }
}

/** The ids a step records with its outcome — see `recordStep`. */
export interface StepIds {
  picked_client_ids?: string[]
  created_client_ids?: string[]
  created_case_ids?: string[]
}
